/* eslint-disable */
import { stringify, ParsedQuery } from 'query-string';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { HttpClient } from 'api/http-client';
import { RequestApprovalFilterApiData } from 'api/resource-request/types';
import { InheritableFieldsInfo, Field } from 'api/tasks';
import type { GroupedFilter } from 'api/tasks/filter-types';
import { SourceType } from 'api/tasks/field-meta';
import type { FieldFilter } from 'api/tasks/filter-types';
import { SelectFilterOperationType } from 'api/tasks/filter-select';
import { AllocatedMinutes } from 'components/organisms/resource-management/types';

const isValidFieldFilter = (filter: FieldFilter): boolean => Boolean(filter?.field);
import { defaultFormat } from 'utils/date-field/formatter';
import { ApiGroupFilter } from 'utils/simple-search-filter/simple-search-filter';
import {
  AggregationMediumMinutes,
  Allocation,
  AllocatedDailyMinutes,
  AllocationBulkPayload,
  AllocationBulkResponse,
  AllocationSplitPayload,
  AllocationMedium,
  AllocationsGroupByTeam,
  AllocationsGroupByUser,
  AllocationsGroupByUsersOrTeams,
  AllocationSource,
  AvailabilityFilterApiWithPlanningLevel,
  AvailabilityForTeamWithProject,
  ConvertAllocationsPropsApi,
  DeleteAllocationsProps,
  FilteredResponseInPeople,
  FilteredResponseInProjects,
  FilteredRoleInPeople,
  FilteredUserInPeople,
  GetPlaceholdersPayload,
  GetPlaceholdersResponse,
  GetResourceAllocationDemandPayload,
  GetResourceAllocationPayload,
  PaginatedPeoplePayload,
  PaginatedPeopleResponse,
  PaginatedProjectsPayload,
  PaginatedProjectsResponse,
  PaginatedProjectsForUserPayload,
  PaginatedProjectsForUserResponse,
  PlanningLevelConfig,
  ResourceAllocationDemandGraphResponse,
  ResourceAllocationDemandResponse,
  ResourceManagementApiZoomType,
  ResourceManagementConfig,
  ResourceManagementExportParams,
  ResourceManagementFilterParams,
  ResourceManagementFilterResponse,
  ResourceManagementGroupBy,
  ResourceManagementPermissions,
  ResourceManagementPermissionsResponse,
  ResourceType,
  RMPermissionsApiParamType,
  TeamLevelAggregation,
  UserLevelAggregation,
  PaginatedResourcesForProjectPayload,
  PaginatedResourcesForProjectResponseData,
  GetPartnersResponse,
  GetPartnersPayload,
  rmFilterToApiFormat,
} from './types';

const baseUrl = '/resource-allocations';

export type AllocationPreviewVariableFieldsResponse = {
  fields: Field[];
  nestedUserFields: Field[];
};

export const getInitialAllocatedMinutes = (): AllocatedMinutes => ({
  [AllocationMedium.Hard]: 0,
  [AllocationMedium.Soft]: 0,
});

const computeAggregateMinutesSplit = (
  mediumMinutesTypes: AggregationMediumMinutes[],
): AllocatedMinutes =>
  mediumMinutesTypes.reduce(
    (result, { type, totalMinutes }) => ({
      ...result,
      [type]: totalMinutes,
    }),
    getInitialAllocatedMinutes(),
  );

export const getUserAggregationsFromApi = (
  aggregations: UserLevelAggregation[],
): UserLevelAggregation[] =>
  aggregations.map(
    ({ roleId, referenceTeamId, totalMinutes, mediumMinutes, ...rest }) => ({
      ...rest,
      ...(referenceTeamId
        ? {
            roleId,
            referenceTeamId,
          }
        : {}),
      type: ResourceType.User,
      allocatedMintutes:
        rest?.projectId === -1
          ? {
              [AllocationMedium.Hard]: totalMinutes ?? 0,
              [AllocationMedium.Soft]: 0,
            }
          : computeAggregateMinutesSplit(mediumMinutes?.types ?? []),
    }),
  );

export const getTeamAggregationsFromApi = (
  aggregations: TeamLevelAggregation[],
): TeamLevelAggregation[] =>
  aggregations.map(({ mediumMinutes, ...rest }) => ({
    ...rest,
    type: ResourceType.Team,
    allocatedMintutes: computeAggregateMinutesSplit(mediumMinutes?.types ?? []),
  }));

export const addUserTypeToAllocation = (
  allocationByUser: AllocationsGroupByUser,
): AllocationsGroupByUser => ({
  ...allocationByUser,
  type: ResourceType.User,
  allocations: allocationByUser.allocations.map(
    ({ role, referenceTeam, ...rest }) => ({
      ...rest,
      ...(referenceTeam != null
        ? {
            referenceTeam,
            role,
          }
        : {}),
      type: ResourceType.User,
    }),
  ),
  aggregations: getUserAggregationsFromApi(allocationByUser.aggregations),
});

export const addTeamTypeToAllocation = (
  allocationByTeam: AllocationsGroupByTeam,
): AllocationsGroupByTeam => ({
  ...allocationByTeam,
  type: ResourceType.Team,
  allocations: allocationByTeam.allocations.map((a) => ({
    ...a,
    type: ResourceType.Team,
  })),
  aggregations: getTeamAggregationsFromApi(allocationByTeam.aggregations),
});

const addUserTypeToFilteredResource = (
  filteredResources: FilteredUserInPeople,
): FilteredUserInPeople => ({
  ...filteredResources,
  type: ResourceType.User,
});

const addRoleTypeToFilteredResource = (
  filteredResources: FilteredRoleInPeople,
): FilteredRoleInPeople => ({
  ...filteredResources,
  type: ResourceType.Role,
});

const getRoleIdsFromFilter = (filter: GroupedFilter): number[] => {
  const roleIds = (
    filter?.groupCriteria?.find?.(
      (x) =>
        x.criteria?.field?.fieldName === 'role' &&
        ![
          SelectFilterOperationType.IsNot,
          SelectFilterOperationType.IsNoneOf,
        ].includes(x.criteria.type as SelectFilterOperationType),
    )?.criteria as any
  )?.value as number[];

  return (Array.isArray(roleIds) ? roleIds : [roleIds]).filter(Boolean);
};

export interface GetMatchedUsersForTeamParams {
  projectId: number;
  teamId: number;
  roleIds: number[];
  filter: SimpleSearchFilter;
  sort?: string;
  ignoreSoftAllocation?: boolean;
}

export interface GetMatchedUsersForTeamResponse {
  data: RequestApprovalFilterApiData[];
  count: number;
}

export interface FindTeamMembersTimelinePayload {
  resourceRequestMapping: {
    resourceRequestType: 'PLACEHOLDER';
    projectId: number;
    resourceRequest: {
      teams: Array<{
        teamId: number;
        teamName?: string;
        roleId?: number;
        identityType?: string;
      }>;
    };
    resourceApproval: {
      roles: number[];
      meta?: {
        teamMemberFields?: SimpleSearchFilter;
      };
    };
  };
  resourceAllocationLiteRequest: {
    groupBy?: string;
    userIdList?: number[];
    startDate: string;
    endDate: string;
    includeSummaries?: boolean;
    includeEmptySummaries?: boolean;
    includeOverAllocations?: boolean;
  };
}

class ResourceManagementClient extends HttpClient {
  public getResourceAllocations = async (
    zoomLevel: ResourceManagementApiZoomType,
    payload: GetResourceAllocationPayload,
    filterString: string,
    signal?: AbortSignal,
  ): Promise<AllocationsGroupByUsersOrTeams> =>
    this.post(
      `${baseUrl}/lite?zoomLevel=${zoomLevel}&useOtherProject=true&useOtherTeamMember=true${
        filterString ? `&${filterString}` : ''
      }`,
      payload,
      { signal },
    );

  public getResourceAllocation = async (
    id: number,
  ): Promise<Allocation & { workingDays?: number }> =>
    this.get(`${baseUrl}/${id}`);

  public getResourceAllocationInheritableFieldValues = async (
    projectIds: number[],
    taskIds: number[],
    userIds: number[],
    phaseIds: number[],
  ): Promise<InheritableFieldsInfo> => {
    return this.get(
      `${baseUrl}/inheritable-field-values?${stringify(
        {
          projectIds: projectIds.join(','),
          taskIds: taskIds.join(','),
          userIds: userIds.join(','),
          phaseIds: phaseIds.join(','),
        },
        {
          skipNull: true,
          skipEmptyString: false,
        },
      )}`,
    );
  };

  public getResourceAllocationsForPeople = async (
    zoomLevel: ResourceManagementApiZoomType,
    payload: GetResourceAllocationPayload,
    filter: GroupedFilter | undefined,
    signal?: AbortSignal,
  ): Promise<AllocationsGroupByUsersOrTeams> => {
    const filterString = filter
      ? ApiGroupFilter.toQueryStringFormatForGroupsForSourceType(
          filter.groupCriteria,
          SourceType.ResourceAllocation,
        )
      : '';

    return this.getResourceAllocations(
      zoomLevel,
      {
        ...payload,
        groupBy: ResourceManagementGroupBy.Users,
        includeSummaries: true,
        includeOverAllocations: true,
        includeEmptySummaries: true,
      },
      filterString,
      signal,
    ).then((x) => {
      return {
        ...x,
        resources: x.resources.map((r) =>
          (r as any).user != null
            ? addUserTypeToAllocation(r as AllocationsGroupByUser)
            : addTeamTypeToAllocation(r as AllocationsGroupByTeam),
        ),
      };
    });
  };

  public getResourceAllocationDemand = async ({
    startDate,
    endDate,
    zoomLevel,
    roleIdList,
    ignoreSoftAllocation,
    filter,
    isHoursView,
  }: GetResourceAllocationDemandPayload): Promise<ResourceAllocationDemandResponse> => {
    // Doing this as for RM, BE needs role as role type instead of user type
    // in order to filter placeholders and replaced users as well
    const transformedFilterString = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria.filter((x) => isValidFieldFilter(x.criteria)),
    ).replaceAll('user*role', 'role*role');

    const roleIds = getRoleIdsFromFilter(filter);

    return this.post(
      `${baseUrl}/demand-new?${transformedFilterString}${
        filter.groupCriteria.length > 0 ? '&' : ''
      }${stringify(
        {
          match: filter.groupCriteria.length ? filter.type : null,
          isHoursView,
        },
        { skipNull: true },
      )}`,
      {
        startDate,
        endDate,
        zoomLevel,
        roleIdList: roleIds.length > 0 ? roleIds : roleIdList,
        ignoreSoftAllocation,
      },
    );
  };

  public getResourceAllocationDemandGraph = async ({
    startDate,
    endDate,
    zoomLevel,
    roleIdList,
    ignoreSoftAllocation,
    filter,
    isHoursView,
  }: GetResourceAllocationDemandPayload): Promise<ResourceAllocationDemandGraphResponse> => {
    // Doing this as for RM, BE needs role as role type instead of user type
    // in order to filter placeholders and replaced users as well
    const transformedFilterString = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria.filter((x) => isValidFieldFilter(x.criteria)),
    ).replaceAll('user*role', 'role*role');

    const roleIds = getRoleIdsFromFilter(filter);

    return this.post(
      `${baseUrl}/demand-graph?${transformedFilterString}${
        filter.groupCriteria.length > 0 ? '&' : ''
      }${stringify(
        {
          match: filter.groupCriteria.length ? filter.type : null,
          isHoursView,
        },
        { skipNull: true },
      )}`,
      {
        startDate,
        endDate,
        zoomLevel,
        roleIdList: roleIds.length > 0 ? roleIds : roleIdList,
        ignoreSoftAllocation,
      },
    );
  };

  public bulkAction = async (
    payload: AllocationBulkPayload,
    requestId: string,
  ): Promise<any> =>
    this.post(`${baseUrl}/bulk-action`, payload, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'X-Request-Id': requestId },
    });

  public updateResourceAllocationStartEndDates = async (
    resourceAllocationId: number,
    { startDate, endDate }: { startDate: string; endDate: string },
  ): Promise<AllocationBulkResponse> =>
    this.patch(
      `${baseUrl}/${resourceAllocationId}/drag-and-drop?${stringify({
        startDate,
        endDate,
      })}`,
      {},
    );

  public splitResourceAllocation = async (
    zoomLevel: ResourceManagementApiZoomType,
    payload: AllocationSplitPayload,
  ): Promise<AllocationBulkResponse> =>
    this.post(`${baseUrl}/split?zoomLevel=${zoomLevel}`, payload);

  public getResourceAvailaibility(
    projectId: number,
    teamId: number,
  ): Promise<AvailabilityForTeamWithProject> {
    return this.post(
      `${baseUrl}/check-availability?projectId=${projectId}&teamId=${teamId}`,
    );
  }

  public getResourceAvailaibilityForTeamPlanner({
    projectId,
    teamId,
    startDate,
    endDate,
    allocatedMinutes,
    roleId,
    teamPlanner,
  }): Promise<AvailabilityForTeamWithProject> {
    const url = `${baseUrl}/check-availability-v2`;

    return this.post(url, {
      teamId,
      projectId,
      startDate,
      endDate,
      allocatedMinutes,
      roleId,
      teamPlanner,
    });
  }

  public findAvailableResources({
    startDate,
    endDate,
    availableMinutes,
    isHoursPerWeek,
    ignoreSoftAllocation,
    roles,
    users,
  }: AvailabilityFilterApiWithPlanningLevel): Promise<number[]> {
    return this.post(`${baseUrl}/find-availability`, {
      availableMinutes,
      startDate: defaultFormat(startDate),
      endDate: defaultFormat(endDate),
      planningLevelConfig: isHoursPerWeek
        ? PlanningLevelConfig.HoursPerWeek
        : PlanningLevelConfig.HoursPerDay,
      ignoreSoftAllocation,
      roles,
      users,
    });
  }

  public getFilteredResources = ({
    filter,
    groupBy,
  }: ResourceManagementFilterParams): Promise<ResourceManagementFilterResponse> => {
    // Doing this as for RM, BE needs role as role type instead of user type
    // in order to filter placeholders and replaced users as well
    const transformedFilterString = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.groupCriteria,
    ).replace('user*role', 'role*role');

    return this.get(
      `${baseUrl}/filter-resources?${transformedFilterString}${
        filter.groupCriteria.length > 0 ? '&' : ''
      }${stringify(
        {
          groupBy,
          match: filter.groupCriteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    );
  };

  public getFilteredResourcesInPeople = (
    filter: GroupedFilter,
  ): Promise<FilteredResponseInPeople> =>
    this.getFilteredResources({
      groupBy: ResourceManagementGroupBy.Users,
      filter,
    }).then((data) => ({
      peopleTabEntities: (
        data as FilteredResponseInPeople
      ).peopleTabEntities.map((x) =>
        (x as any).userId != null
          ? addUserTypeToFilteredResource(x as FilteredUserInPeople)
          : addRoleTypeToFilteredResource(x as FilteredRoleInPeople),
      ),
    }));

  public getFilteredResourcesInProjects = (
    filter: GroupedFilter,
  ): Promise<FilteredResponseInProjects> =>
    this.getFilteredResources({
      groupBy: ResourceManagementGroupBy.Projects,
      filter,
    }).then((data) => data as FilteredResponseInProjects);

  public deleteResourceAllocations = ({
    projectId,
    resourceIdentifier,
  }: DeleteAllocationsProps): Promise<void> =>
    this.delete(`${baseUrl}`, {
      data: {
        projectId,
        ...(resourceIdentifier.type === ResourceType.User
          ? {
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
              projectFinancialsBudgetId:
                resourceIdentifier?.projectFinancialsBudgetId,
            }
          : {
              teamId: resourceIdentifier.teamId,
              projectFinancialsBudgetId:
                resourceIdentifier?.projectFinancialsBudgetId,
            }),
      },
    });

  public convertResourceAllocations = ({
    projectId,
    resourceIdentifier,
    medium,
  }: ConvertAllocationsPropsApi): Promise<void> =>
    this.put(`${baseUrl}`, {
      projectId,
      ...(resourceIdentifier
        ? resourceIdentifier.type === ResourceType.User
          ? {
              userId: resourceIdentifier.userId,
              roleId: resourceIdentifier.roleId,
              referenceTeamId: resourceIdentifier.referenceTeamId,
              projectFinancialsBudgetId:
                resourceIdentifier?.projectFinancialsBudgetId,
            }
          : {
              teamId: resourceIdentifier.teamId,
              projectFinancialsBudgetId:
                resourceIdentifier?.projectFinancialsBudgetId,
            }
        : {}),
      medium,
    });

  /** Resource Management Config */
  public getResourceManagementConfig =
    async (): Promise<ResourceManagementConfig> => {
      const data: ResourceManagementConfig =
        await this.get<ResourceManagementConfig>(`${baseUrl}/configuration`);

      return data;
    };

  public updateResourceManagementConfig = async (
    payload: ResourceManagementConfig,
  ): Promise<ResourceManagementConfig> =>
    this.put(`${baseUrl}/configuration`, payload);

  public updateResourceManagementPlanningLevel = async (
    payload: string,
  ): Promise<ResourceManagementConfig> =>
    this.put(`${baseUrl}/configuration/update-planning-level`, payload, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': 'text/plain' },
    });

  public getVariableFields =
    async (): Promise<AllocationPreviewVariableFieldsResponse> =>
      this.get(`${baseUrl}/configuration/variable-fields`);

  public getSourceTasksForAllocationId = async (
    allocationId: number,
  ): Promise<AllocationSource[]> =>
    this.get<unknown, { sources: AllocationSource[] }>(
      `${baseUrl}/${allocationId}/sources`,
    ).then((x) => x.sources);

  public exportAllocations = async ({
    startDate,
    endDate,
    groupBy,
    zoomType,
    filter,
  }: ResourceManagementExportParams): Promise<string> =>
    this.get<{ downloadUrl: string }>(
      `/reports/resource-allocation/export?${ApiGroupFilter.toQueryStringFormatForGroups(
        filter.groupCriteria,
      )}${filter.groupCriteria.length > 0 ? '&' : ''}${stringify(
        {
          startDate: defaultFormat(startDate),
          endDate: defaultFormat(endDate),
          groupBy,
          zoomType,
          match: filter.groupCriteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    ).then((response) => response.downloadUrl);

  public getDailyMinutesForProject = async (
    startDate: string,
    userId: number,
    projectIds: number[],
  ): Promise<AllocatedDailyMinutes[]> =>
    this.get(
      `${baseUrl}/${startDate}/daily-minutes?userId=${userId}&projectIds=${projectIds.join(
        ',',
      )}`,
    );

  public getResourceMangementPermissions =
    async (): Promise<ResourceManagementPermissions> =>
      this.get(`${baseUrl}/rbac`);

  public getAccesibleUsersFromRM = async (
    queryParams: ParsedQuery = {},
  ): Promise<ResourceManagementPermissionsResponse> =>
    this.get(`${baseUrl}/user-access?${stringify(queryParams)}`);

  public getAccesibleProjectsFromRM = async (
    queryParams: ParsedQuery = {},
  ): Promise<ResourceManagementPermissionsResponse> =>
    this.get(`${baseUrl}/project-access?${stringify(queryParams)}`);

  public getAccesibleProjectsForResourceAI = async (): Promise<number[]> =>
    this.get(
      `${baseUrl}/project-access?source=${RMPermissionsApiParamType.ResourceAI}`,
    );

  public getAccesiblePeopleForResourceAI = async (): Promise<number[]> =>
    this.get(
      `${baseUrl}/user-access?source=${RMPermissionsApiParamType.ResourceAI}`,
    );

  public getAccesibleProjectsForResourceRequest = async (): Promise<number[]> =>
    this.get(`${baseUrl}/project-access?source=RESOURCE_REQUEST`);

  public getMatchedUsersForTeam = async (
    params: GetMatchedUsersForTeamParams & { limit?: number; offset?: number },
  ): Promise<GetMatchedUsersForTeamResponse> => {
    const { projectId, teamId, limit, offset, ignoreSoftAllocation, ...rest } =
      params;

    let url = `${baseUrl}/find-team-members`;

    // Add pagination query parameters if provided
    const queryParams: string[] = [];

    if (limit !== undefined && offset !== undefined) {
      queryParams.push(`offset=${offset}&limit=${limit}`);
    }
    if (ignoreSoftAllocation !== undefined) {
      queryParams.push(`ignoreSoftAllocation=${ignoreSoftAllocation}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.post(url, {
      ...rest,
      projectId,
      teamId,
    });
  };

  public getMatchedTeamMembersForTimeline = async (
    zoomLevel: ResourceManagementApiZoomType,
    allocationParams: {
      ignoreSoftAllocation: boolean;
      offset?: number;
      limit?: number;
    },
    payload: FindTeamMembersTimelinePayload,
  ): Promise<AllocationsGroupByUsersOrTeams> => {
    const queryParams = [
      `zoomLevel=${zoomLevel}`,
      `ignoreSoftAllocation=${allocationParams.ignoreSoftAllocation}`,
    ];

    if (allocationParams.offset !== undefined) {
      queryParams.push(`offset=${allocationParams.offset}`);
    }
    if (allocationParams.limit !== undefined) {
      queryParams.push(`limit=${allocationParams.limit}`);
    }

    const response = (await this.post(
      `${baseUrl}/find-team-members/timeline?${queryParams.join('&')}`,
      payload,
    )) as AllocationsGroupByUsersOrTeams;

    return {
      ...response,
      resources: response.resources.map((r) =>
        (r as any).user != null
          ? addUserTypeToAllocation(r as AllocationsGroupByUser)
          : addTeamTypeToAllocation(r as AllocationsGroupByTeam),
      ),
    };
  };

  public getPaginatedPeople = async (
    payload: PaginatedPeoplePayload,
    signal?: AbortSignal,
  ): Promise<PaginatedPeopleResponse> => {
    const updatedPayload = {
      ...payload,
      filter: rmFilterToApiFormat(payload.filter),
    };

    return this.post<PaginatedPeopleResponse, PaginatedPeoplePayload>(
      '/projects/rm-people-projects',
      updatedPayload,
      { signal },
    );
  };

  public getPaginatedProjects = async (
    payload: PaginatedProjectsPayload,
    signal,
  ): Promise<PaginatedProjectsResponse> => {
    const updatedPayload = rmFilterToApiFormat(payload);

    return this.get<PaginatedProjectsResponse>(
      `/projects/rm-light?${stringify(updatedPayload)}`,
      {signal}
    );
  };
  public getPaginatedProjectsForUser = async (
    payload: PaginatedProjectsForUserPayload,
    signal?: AbortSignal,
  ): Promise<PaginatedProjectsForUserResponse> => {
    const updatedPayload = {
      ...payload,
      filter: rmFilterToApiFormat(payload.filter),
    };
    return this.post<
      PaginatedProjectsForUserResponse,
      PaginatedProjectsForUserPayload
    >('/projects/rm-people-projects', updatedPayload, { signal });
  };
  public getPlaceholdersCount = async (
    payload: GetPlaceholdersPayload,
    signal?: AbortSignal,
  ): Promise<GetPlaceholdersResponse> => {
    const updatedPayload = {
      ...payload,
      filter: rmFilterToApiFormat(payload.filter),
    };

    return this.post<GetPlaceholdersResponse,GetPlaceholdersPayload>(`/projects/rm-people-projects`, updatedPayload, { signal });
  };

  public getPartnersCount = async (
    payload: GetPartnersPayload,
    signal?: AbortSignal,
  ): Promise<GetPartnersResponse> => {
    const updatedPayload = {
      ...payload,
      filter: rmFilterToApiFormat(payload.filter),
    };
    return this.post<
      GetPartnersResponse,
      GetPartnersPayload
    >(`/projects/rm-people-projects`, updatedPayload, { signal });
  };

  public getPaginatedResourcesForProject = async (
    payload: PaginatedResourcesForProjectPayload,
    signal?: AbortSignal,
  ): Promise<PaginatedResourcesForProjectResponseData> => {
    const project =  payload.projects?.[0];
    const updatedPayload = rmFilterToApiFormat({
      ...payload.filter,
      offset: project?.offset,
    });

    let url;

    if (project?.budgetId) {
      url = `projects/${project?.projectId}/budgets/${project?.budgetId}/rm-members-paginated?${stringify(updatedPayload)}`
    } else {
      url = `projects/${project?.projectId}/rm-members-paginated?${stringify(updatedPayload)}`
    }

    return this.get<PaginatedResourcesForProjectResponseData>(url, {
      signal,
    });
  };
}

export const ResourceManagmentApi = new ResourceManagementClient();
